import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { AlertTriangle, CheckCircle } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { z } from 'zod';

import { AuthLayout } from '../../components/auth-layout/AuthLayout';
import { WhatHappensNext } from '../../components/auth-layout/what-happens-next';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Form, FormControl, FormField, FormItem, FormMessage } from '../../components/ui/form';
import { Input } from '../../components/ui/input';
import { COINIS_LOGO_DARK } from '../../config';
import { authService, SsoProvider } from '../../services/auth';
import { generateErrorMessages } from '../../utils/showApiErrors';
import { SsoButton } from '../setup/components/SsoButton';
import { emailSchema } from '../setup/schemas/email';

type RegisterView = 'form' | 'check-email' | 'already-registered' | 'sso-conflict';

const PROVIDER_LABELS: Record<SsoProvider, string> = { google: 'Google', facebook: 'Facebook' };

const registerFormSchema = z.object({
  ...emailSchema.shape
});

type RegisterFormValues = z.infer<typeof registerFormSchema>;

export const Register = (): JSX.Element => {
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const isExpired = params.get('expired') === 'true';
  const expiredEmail = params.get('email') ?? '';

  const [view, setView] = useState<RegisterView>('form');
  const [loading, setLoading] = useState(false);
  const [ssoAuthLoading, setSsoAuthLoading] = useState<SsoProvider | null>(null);
  const [conflictProvider, setConflictProvider] = useState<SsoProvider>('google');
  const [error, setError] = useState<string | string[] | null>(null);
  const [registeredEmail, setRegisteredEmail] = useState(expiredEmail);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  const serviceRedirectUrl = params.get('service_redirect_url') ?? undefined;
  const clientId = params.get('client_id') ?? undefined;
  const autoGoogle = params.get('auto_google') === 'true';
  const autoGoogleFiredRef = useRef(false);
  const autoEmail = params.get('auto_email') === 'true';
  const autoEmailParam = params.get('email') ?? '';
  const autoEmailFiredRef = useRef(false);

  // Collect ALL query params as auth_request_params
  const authRequestParams = useMemo(() => {
    const result: Record<string, string> = {};
    params.forEach((value, key) => {
      result[key] = value;
    });
    return Object.keys(result).length > 0 ? result : undefined;
  }, [params]);

  const form = useForm<RegisterFormValues>({
    defaultValues: { email: autoEmailParam || expiredEmail },
    resolver: zodResolver(registerFormSchema),
    mode: 'onTouched'
  });

  const emailValue = form.watch('email');
  const isEmailValid = emailSchema.safeParse({ email: emailValue }).success;

  const onSubmit = async (values: RegisterFormValues) => {
    setError(null);
    setLoading(true);
    try {
      await authService.registerWithEmail(values.email, authRequestParams);
      setRegisteredEmail(values.email);
      setView('check-email');
    } catch (e) {
      const axiosError = e as AxiosError<any>;
      const detail = axiosError.response?.data?.detail;

      if (axiosError.response?.status === 409 && detail?.code === 'already_registered') {
        setRegisteredEmail(detail.email);
        setView('already-registered');
      } else if (axiosError.response?.status === 400 && typeof detail === 'string') {
        // The backend names the provider in the message; anything else is a
        // plain error.
        const lowered = detail.toLowerCase();
        const provider: SsoProvider | undefined = lowered.includes('facebook')
          ? 'facebook'
          : lowered.includes('google')
          ? 'google'
          : undefined;
        if (provider) {
          setConflictProvider(provider);
          setView('sso-conflict');
        } else {
          setError(generateErrorMessages(axiosError));
        }
      } else {
        setError(generateErrorMessages(axiosError));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSsoAuth = async (provider: SsoProvider) => {
    setSsoAuthLoading(provider);
    try {
      window.location.href = (
        await authService.getSsoAuthUrl(provider, serviceRedirectUrl, clientId, authRequestParams)
      ).url;
    } catch (e) {
      setError(generateErrorMessages(e as AxiosError));
      setSsoAuthLoading(null);
    }
  };

  // Recover from the SSO redirect being cancelled (e.g. "Back" in the account chooser).
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setLoading(false);
        setSsoAuthLoading(null);
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  useEffect(() => {
    if (!autoGoogle || autoGoogleFiredRef.current) {
      return;
    }

    autoGoogleFiredRef.current = true;
    handleSsoAuth('google');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoGoogle]);

  useEffect(() => {
    if (!autoEmail || autoEmailFiredRef.current) {
      return;
    }

    if (!emailSchema.safeParse({ email: autoEmailParam }).success) {
      return;
    }

    autoEmailFiredRef.current = true;
    onSubmit({ email: autoEmailParam });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoEmail, autoEmailParam]);

  const handleResendVerification = async () => {
    setResendLoading(true);
    setResendSuccess(false);
    try {
      await authService.resendVerificationEmail(registeredEmail);
      setResendSuccess(true);
    } catch (e) {
      setError(generateErrorMessages(e as AxiosError));
    } finally {
      setResendLoading(false);
    }
  };

  const renderNonFormContent = () => {
    switch (view) {
      case 'check-email':
        return (
          <>
            <img src={COINIS_LOGO_DARK} alt="Coinis" className="h-7 w-auto" />
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-onboarding-success-light">
                <CheckCircle className="h-5 w-5 text-onboarding-success" />
              </div>
              <div className="font-onboarding-1 text-3xl font-bold tracking-tight text-primary">Check your email</div>
            </div>
            <p className="text-[15px] font-medium leading-relaxed text-[var(--ink-2)]">
              We&apos;ve sent a verification link to{' '}
              <span className="font-semibold text-[var(--ink)]">{registeredEmail}</span>. Follow the instructions to
              complete your registration.
            </p>
            <p className="text-sm text-[var(--ink-2)]">
              Didn&apos;t receive it?{' '}
              <button
                onClick={() => void handleResendVerification()}
                disabled={resendLoading}
                className="cursor-pointer font-semibold text-brand-blue disabled:cursor-not-allowed disabled:opacity-50"
              >
                {resendLoading ? 'Sending...' : 'Resend email'}
              </button>
            </p>
            {resendSuccess && (
              <p className="text-sm font-semibold text-[hsl(var(--onboarding-success))]">
                If the address is registered, you&apos;ll receive an email.
              </p>
            )}
          </>
        );

      case 'already-registered':
        return (
          <>
            <img src={COINIS_LOGO_DARK} alt="Coinis" className="h-7 w-auto" />
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-gold-50">
                <AlertTriangle className="h-5 w-5 text-brand-gold" />
              </div>
              <div className="font-onboarding-1 text-3xl font-bold tracking-tight text-primary">Already registered</div>
            </div>
            <p className="text-[15px] font-medium leading-relaxed text-[var(--ink-2)]">
              An account with <span className="font-semibold text-[var(--ink)]">{registeredEmail}</span> already exists.
              Would you like to resend the verification email?
            </p>
            <button
              onClick={() => void handleResendVerification()}
              disabled={resendLoading}
              className="btn-cta-orange flex h-12 w-full items-center justify-center rounded-lg text-[15px] font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-50"
            >
              {resendLoading ? 'Sending...' : 'Resend verification email'}
            </button>
            <p className="text-center text-sm text-[var(--ink-2)]">
              <a href={`/login${window.location.search}`} className="font-semibold text-brand-blue">
                Go to sign in
              </a>
            </p>
            {resendSuccess && (
              <p className="text-sm font-semibold text-[hsl(var(--onboarding-success))]">
                If the address is registered, you&apos;ll receive an email.
              </p>
            )}
          </>
        );

      case 'sso-conflict':
        return (
          <>
            <img src={COINIS_LOGO_DARK} alt="Coinis" className="h-7 w-auto" />
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-blue/10">
                <AlertTriangle className="h-5 w-5 text-brand-blue" />
              </div>
              <div className="font-onboarding-1 text-3xl font-bold tracking-tight text-primary">
                {PROVIDER_LABELS[conflictProvider]} account
              </div>
            </div>
            <p className="text-[15px] font-medium leading-relaxed text-[var(--ink-2)]">
              This account uses {PROVIDER_LABELS[conflictProvider]} authentication. Please sign in with{' '}
              {PROVIDER_LABELS[conflictProvider]} instead.
            </p>
            <SsoButton
              provider={conflictProvider}
              onClick={() => handleSsoAuth(conflictProvider)}
              loading={ssoAuthLoading === conflictProvider}
            />
            <p className="text-center text-sm text-[var(--ink-2)]">
              <a href={`/login${window.location.search}`} className="font-semibold text-brand-blue">
                Go to sign in
              </a>
            </p>
            <WhatHappensNext />
          </>
        );

      default:
        return null;
    }
  };

  const showAutoGoogleSpinner = autoGoogle && view === 'form' && !error;
  const showAutoEmailSpinner =
    autoEmail && view === 'form' && !error && emailSchema.safeParse({ email: autoEmailParam }).success;

  return (
    <AuthLayout>
      {showAutoGoogleSpinner ? (
        <div className="flex flex-col items-center justify-center gap-4 py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-brand-blue" />
          <p className="text-sm font-medium text-[var(--ink-2)]">Redirecting to Google…</p>
        </div>
      ) : showAutoEmailSpinner ? (
        <div className="flex flex-col items-center justify-center gap-4 py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-brand-blue" />
          <p className="text-sm font-medium text-[var(--ink-2)]">Creating your account…</p>
        </div>
      ) : view !== 'form' ? (
        renderNonFormContent()
      ) : (
        <>
          <img src={COINIS_LOGO_DARK} alt="Coinis" className="h-7 w-auto" />

          <div className="mt-1.5 flex flex-col gap-[11px]">
            <h1 className="font-onboarding-1 text-[2.35rem] font-bold leading-[1.04] tracking-[-0.025em] text-primary max-[519px]:text-[2rem]">
              Turn a sentence into a video ad.
            </h1>
            <p className="text-[15.5px] leading-[1.5] text-[var(--ink-2)]">
              Describe what you&apos;re selling. Coinis writes, designs, and renders a scroll-stopping ad — ready to
              launch. No editing. No agency.
            </p>
          </div>

          <div className="flex flex-col gap-2.5">
            <SsoButton
              provider="google"
              onClick={() => handleSsoAuth('google')}
              loading={ssoAuthLoading === 'google'}
              disabled={!!ssoAuthLoading}
            />
            <SsoButton
              provider="facebook"
              onClick={() => handleSsoAuth('facebook')}
              loading={ssoAuthLoading === 'facebook'}
              disabled={!!ssoAuthLoading}
            />
          </div>

          <div className="flex items-center gap-[14px] text-[13px] font-medium text-[var(--ink-3)]">
            <span className="h-px flex-1 bg-[var(--line)]" />
            or
            <span className="h-px flex-1 bg-[var(--line)]" />
          </div>

          {isExpired && (
            <Alert className="border-brand-gold/30 bg-brand-gold-50">
              <AlertTriangle className="h-4 w-4 text-brand-gold" />
              <AlertDescription className="text-brand-orange">
                Your verification link has expired. Enter your email to receive a new one.
              </AlertDescription>
            </Alert>
          )}

          <Form {...form}>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void form.handleSubmit(onSubmit)(e);
              }}
              className="flex flex-col gap-3.5"
              noValidate
            >
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <div className="relative flex items-center">
                      <span className="pointer-events-none absolute left-[15px] z-10 flex text-[var(--ink-3)]">
                        <svg
                          width="17"
                          height="17"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <rect x="2" y="4" width="20" height="16" rx="2" />
                          <path d="m22 7-10 5L2 7" />
                        </svg>
                      </span>
                      <FormControl>
                        <Input
                          type="email"
                          autoComplete="email"
                          autoCapitalize="none"
                          spellCheck={false}
                          placeholder="you@yourbrand.com"
                          className="pl-11"
                          {...field}
                        />
                      </FormControl>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {error && (
                <div className="text-sm font-medium text-loss">
                  {Array.isArray(error) ? error.map((msg, i) => <div key={i}>{msg}</div>) : error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !isEmailValid}
                className="btn-cta-orange mt-2 flex h-12 w-full items-center justify-center rounded-lg text-[15px] font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white" />
                    <span>Creating account...</span>
                  </div>
                ) : (
                  'Create free account'
                )}
              </button>

              <div className="flex flex-wrap items-center gap-[7px] text-[12.5px] font-medium text-[var(--ink-3)]">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-[hsl(var(--onboarding-success))]"
                >
                  <path d="M20 6 9 17l-5-5" />
                </svg>
                Free to start
                <span className="h-[3px] w-[3px] rounded-full bg-[var(--ink-3)] opacity-60" />
                No credit card
                <span className="h-[3px] w-[3px] rounded-full bg-[var(--ink-3)] opacity-60" />
                Ready in 2 minutes
              </div>
            </form>
          </Form>

          <p className="text-center text-[13.5px] font-medium text-[var(--ink-2)]">
            Already with us?{' '}
            <a href={`/login${window.location.search}`} className="font-bold text-primary hover:underline">
              Log in
            </a>
          </p>

          <p className="text-center text-[11.5px] leading-[1.5] text-[var(--ink-3)]">
            By continuing you agree to our{' '}
            <a
              href="https://coinis.com/terms-of-use"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[var(--ink-2)] underline underline-offset-2"
            >
              Terms
            </a>{' '}
            and{' '}
            <a
              href="https://coinis.com/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[var(--ink-2)] underline underline-offset-2"
            >
              Privacy Policy
            </a>
            .
          </p>
        </>
      )}
    </AuthLayout>
  );
};
